/**
 * PostHog product analytics for the web app (2026-10-23).
 *
 * Off unless the build has REACT_APP_POSTHOG_KEY and REACT_APP_POSTHOG_HOST (set in .env.production
 * only), so local dev, Jest and the e2e build send nothing. Every helper here is a no-op until
 * initAnalytics() has run.
 *
 * It records how staff use the app, never who a candidate is (gotcha #258). Text is kept only
 * inside the app's own controls (UI_TEXT: buttons, icon buttons, tabs, toggle buttons, table headers,
 * form labels, and anything marked `ph-ui`). Everywhere else replays mask it and click events drop
 * it, so a name in a table cell, card or chip never leaves the browser. A control that does show a
 * candidate's name gets `ph-sensitive`, which masks it and stops PostHog recording its clicks.
 * URLs keep their path and the parameters in SAFE_QUERY_PARAMS (ids and modes); any other value
 * becomes "masked" and a link off the app keeps only its host. Email addresses are masked in all
 * event text, inputs and images are never recorded, and the page title is dropped (profile pages
 * put the candidate's name in it).
 */
import posthog, { type CaptureResult, type CapturedNetworkRequest, type Properties } from "posthog-js";

export interface AnalyticsUser {
  sub: string;
  email: string;
  name: string;
}

/** On a UI control whose text is a candidate's name, email or photo: masked, and its clicks are not recorded. */
export const SENSITIVE_CLASS = "ph-sensitive";
/** On a region whose text is all UI (navigation, a filter drawer): replays and clicks keep it. */
export const UI_CLASS = "ph-ui";

/**
 * Menus and select values are left out on purpose: they are the app's data-driven lists (candidates,
 * contracts, staff), so they stay masked; mark a menu of fixed choices `ph-ui` to keep its text.
 */
const UI_TEXT = {
  classes: [
    UI_CLASS, "MuiButton-root", "MuiIconButton-root", "MuiTab-root", "MuiToggleButton-root",
    "MuiTableSortLabel-root", "MuiTableCell-head", "MuiFormLabel-root", "MuiInputLabel-root", "MuiFormControlLabel-label",
  ],
  tags: ["th", "label", "legend"],
  roles: ["tab", "columnheader"],
};
const UI_TEXT_SELECTOR = [
  ...UI_TEXT.classes.map((c) => `.${c}`),
  ...UI_TEXT.tags,
  ...UI_TEXT.roles.map((r) => `[role="${r}"]`),
].join(", ");

/** Query parameters whose values are ids, enums or numbers (page and API URLs). Any other value is masked. */
const SAFE_QUERY_PARAMS = new Set([
  "tab", "view", "type", "status", "step", "stale", "role", "event", "cluster", "regions", "draft", "thread",
  "thread_key", "overdue_only", "deferred_due", "oppId", "shortlistOpp", "shortlistSort", "shortlistView",
  "shortlistRank", "talent_id", "profileId", "company_id", "contract_id", "ids", "round", "party", "eventType",
  "page", "pageSize", "limit", "days", "weeks", "granularity", "dateFrom", "dateTo", "fresh", "refresh", "_cb",
]);

/** Attributes that hold readable text rather than layout: kept or masked like the element's text. */
const TEXT_ATTRIBUTES = new Set(["title", "aria-label", "alt", "placeholder", "aria-description", "aria-valuetext", "label"]);
const URL_ATTRIBUTES = new Set(["href", "src"]);

const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const MASKED = "masked";

let enabled = false;

export function maskEmails(text: string): string {
  return text.replace(EMAIL, "<email>");
}

function maskAll(text: string): string {
  return text.replace(/\S/g, "*");
}

/** True when an element's text is the app's own UI, not data about a candidate. */
export function isUiText(element: Element | null | undefined): boolean {
  return !!element && !element.closest(`.${SENSITIVE_CLASS}`) && !!element.closest(UI_TEXT_SELECTOR);
}

function ownHosts(): Set<string> {
  const hosts = new Set<string>([window.location.host]);
  const api = /^https?:\/\/([^/?#]+)/i.exec(process.env.REACT_APP_API_URL || "");
  if (api) hosts.add(api[1]);
  return hosts;
}

/**
 * A URL as PostHog may keep it: the path, safe query values, a plain `#anchor`; off-app links keep
 * only their origin (a candidate's LinkedIn, a signed S3 link); mailto/tel are masked. Non-URLs pass.
 */
export function scrubUrl(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const scheme = /^(mailto|tel|sms):/i.exec(value);
  if (scheme) return `${scheme[1].toLowerCase()}:${MASKED}`;
  const absolute = /^https?:\/\//i.test(value);
  if (!absolute && !value.startsWith("/")) return value;
  let url: URL;
  try {
    url = new URL(value, "http://relative.invalid");
  } catch {
    return MASKED;
  }
  if (absolute && !ownHosts().has(url.host)) return url.origin;
  for (const key of Array.from(new Set(url.searchParams.keys()))) {
    if (!SAFE_QUERY_PARAMS.has(key)) url.searchParams.set(key, MASKED);
  }
  const rest = `${url.pathname}${url.search}${/^#[\w-]*$/.test(url.hash) ? url.hash : ""}`;
  return absolute ? `${url.origin}${rest}` : rest;
}

/* ── click events: `$elements_chain` is one `tag.class.class:key="value"…` segment per element, `;`-joined ── */

interface ChainElement {
  selector: string;
  attributes: Array<[string, string]>;
}

export function parseElementsChain(chain: string): ChainElement[] {
  const parts: string[] = [];
  let current = "";
  let quoted = false;
  for (let i = 0; i < chain.length; i++) {
    const ch = chain[i];
    if (ch === "\\") {
      current += ch + (chain[i + 1] ?? "");
      i++;
    } else if (ch === ";" && !quoted) {
      parts.push(current);
      current = "";
    } else {
      if (ch === '"') quoted = !quoted;
      current += ch;
    }
  }
  parts.push(current);
  return parts.map((part) => {
    const colon = part.indexOf(":");
    const attributes: Array<[string, string]> = [];
    const re = /([^=":]+)="((?:[^"\\]|\\.)*)"/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(colon === -1 ? "" : part.slice(colon + 1)))) attributes.push([m[1], m[2]]);
    return { selector: colon === -1 ? part : part.slice(0, colon), attributes };
  });
}

export function stringifyElementsChain(elements: ChainElement[]): string {
  return elements.map((e) => `${e.selector}:${e.attributes.map(([k, v]) => `${k}="${v}"`).join("")}`).join(";");
}

function chainElementIs(e: ChainElement, classes: string[], tags: string[], roles: string[]): boolean {
  const [tag, ...elementClasses] = e.selector.split(".");
  const role = e.attributes.find(([k]) => k === "attr__role")?.[1];
  return tags.includes(tag) || elementClasses.some((c) => classes.includes(c)) || (!!role && roles.includes(role));
}

/** A value inside the chain is escaped (`\"`): unescape, change, escape again. */
function editChainValue(value: string, edit: (v: string) => string): string {
  return edit(value.replace(/\\"/g, '"')).replace(/"/g, '\\"');
}

/** Keeps a click's text only when it sits inside a UI control; scrubs every href. */
export function scrubClickProperties(props: Properties): Properties {
  const chain = parseElementsChain(String(props.$elements_chain));
  const keepText = chain.some((e) => chainElementIs(e, UI_TEXT.classes, UI_TEXT.tags, UI_TEXT.roles))
    && !chain.some((e) => chainElementIs(e, [SENSITIVE_CLASS], [], []));
  for (const element of chain) {
    element.attributes = element.attributes
      .filter(([k]) => keepText || (k !== "text" && !TEXT_ATTRIBUTES.has(k.replace(/^attr__/, ""))))
      .map(([k, v]) => [k, editChainValue(v, k === "href" || k === "attr__href" ? (u) => String(scrubUrl(u)) : maskEmails)]);
  }
  const out: Properties = { ...props, $elements_chain: stringifyElementsChain(chain) };
  delete out.$elements; // the array twin of the chain, sent only when the chain isn't a string
  if (!keepText) delete out.$el_text;
  return out;
}

/* ── every event ── */

function scrubProperties(props: Properties | undefined, maskEmailText: boolean): Properties | undefined {
  if (!props) return props;
  const out: Properties = {};
  for (const [key, value] of Object.entries(props)) {
    if (key === "$title") continue;
    if (key === "$heatmap_data" && value && typeof value === "object") {
      out[key] = Object.fromEntries(Object.entries(value).map(([url, data]) => [scrubUrl(url), data]));
      continue;
    }
    const scrubbed = scrubUrl(value);
    out[key] = maskEmailText && typeof scrubbed === "string" ? maskEmails(scrubbed) : scrubbed;
  }
  return out;
}

/** before_send hook: runs on every event, including the ones autocapture and pageviews create. */
export function scrubEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  const props = typeof event.properties?.$elements_chain === "string" ? scrubClickProperties(event.properties) : event.properties;
  return {
    ...event,
    properties: scrubProperties(props, true) as Properties,
    // Person properties: the signed-in staff member's own email and name stay.
    $set: scrubProperties(event.$set, false),
    $set_once: scrubProperties(event.$set_once, false),
  };
}

/* ── session replay ── */

/** Replay text: UI keeps its words (emails masked), everything else becomes `*`. */
export function maskReplayText(text: string, element?: Element | null): string {
  return isUiText(element) ? maskEmails(text) : maskAll(text);
}

/** Replay attributes: text-like ones follow the element's text, URLs are scrubbed, layout passes. */
export function maskReplayAttribute(name: string, value: string, element?: Element | null): string {
  if (URL_ATTRIBUTES.has(name)) return String(scrubUrl(value));
  if (TEXT_ATTRIBUTES.has(name)) return maskReplayText(value, element);
  return value;
}

/** Replay network capture (if turned on in PostHog): API URLs carry ?search= / ?email=; bodies never leave. */
export function scrubNetworkRequest(request: CapturedNetworkRequest): CapturedNetworkRequest {
  return { ...request, name: String(scrubUrl(request.name)), requestBody: null, responseBody: null };
}

export function initAnalytics(): boolean {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST;
  if (enabled || !key || !host) return enabled;
  posthog.init(key, {
    api_host: host,
    defaults: "2026-05-30",
    person_profiles: "identified_only",
    capture_exceptions: true,
    // Click text and attributes are captured, then scrubbed by scrubEvent (UI controls only).
    mask_all_text: false,
    mask_all_element_attributes: false,
    enable_recording_console_log: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      maskTextFn: maskReplayText,
      maskAttributeFn: maskReplayAttribute,
      blockSelector: "img, video, canvas",
      recordHeaders: false,
      recordBody: false,
      maskCapturedNetworkRequestFn: scrubNetworkRequest,
    },
    before_send: scrubEvent,
  });
  enabled = true;
  return enabled;
}

export function isAnalyticsEnabled(): boolean {
  return enabled;
}

/** Ties this browser's events to the staff member (Cognito sub) who signed in. */
export function identifyUser(user: AnalyticsUser): void {
  if (!enabled || !user.sub) return;
  posthog.identify(user.sub, { email: user.email, name: user.name });
}

/** On sign-out: the next person on this browser starts a fresh anonymous id. */
export function resetUser(): void {
  if (!enabled) return;
  posthog.reset();
}

/** A custom event. Properties must be ids, enums, counts or taxonomy values, never names, emails or free text. */
export function track(event: string, properties?: Properties): void {
  if (!enabled) return;
  try {
    posthog.capture(event, properties);
  } catch {
    // Called from inside shortlist / reject thunks: analytics must never fail the action it reports on.
  }
}
